import { useState } from "react";
import axios from "axios";
import { Loader2, Search } from "lucide-react";
import { AdminLayout } from "../AdminPages";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const btn = "rounded-full px-4 py-1.5 text-xs disabled:opacity-40";

const JourneyRow = ({ j, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const go = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/api/admin/library-restore/journey/${j.id}`, {}, auth()); setDone({ restored: "Restored", already_copied: "Already copied to your library" }[data.action] || "Copied to your library"); onDone(); }
    catch (e) { setDone(e?.response?.data?.detail === "not_copyable" ? "This one has no finished audio to copy" : "Couldn't do that just now"); }
    finally { setBusy(false); }
  };
  const label = j.mine ? (j.trashed ? "Restore" : null) : "Copy to my library";
  const mineNote = j.audio_status === "failed" ? "Yours, but the build failed. Retry it from My Library" : "Already in your library";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-black/10 p-3" data-testid={`restore-journey-${j.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{j.name}</p>
        <p className="text-xs opacity-60">{j.mine ? "Your account" : j.owner_email} · {j.trashed ? "in Recently deleted" : j.audio_status} · {(j.created_at || "").slice(0, 10)}</p>
      </div>
      {done ? <span className="text-xs text-emerald-700" data-testid={`restore-done-${j.id}`}>{done}</span>
        : label ? <button onClick={go} disabled={busy || (!j.mine && !j.copyable)} className={`${btn} bg-amber-300 text-black`} data-testid={`restore-btn-${j.id}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : label}</button>
        : <span className="text-xs opacity-60">{mineNote}</span>}
    </div>
  );
};

const BlueprintRow = ({ b }) => {
  const [state, setState] = useState("");
  const go = async () => {
    setState("busy");
    try { const { data } = await axios.post(`${API}/api/admin/library-restore/blueprint/${b.id}`, {}, auth()); setState(data.audio_status === "ready" ? "Added, ready to play" : "Added, building now"); }
    catch { setState("Couldn't add just now"); }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-black/20 p-3" data-testid={`restore-bp-${b.id}`}>
      <p className="text-sm">{b.label} <span className="text-xs opacity-50">· {b.duration_minutes} min{b.visibility === "bespoke" ? " · private" : ""}</span></p>
      {state && state !== "busy" ? <span className="text-xs text-emerald-700" data-testid={`restore-bp-done-${b.id}`}>{state}</span>
        : <button onClick={go} disabled={state === "busy"} className={`${btn} border border-black/30`} data-testid={`restore-bp-btn-${b.id}`}>{state === "busy" ? <Loader2 size={12} className="animate-spin" /> : "Make a fresh one"}</button>}
    </div>
  );
};

export default function AdminLibraryRestore() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true); setErr("");
    try { setRes((await axios.get(`${API}/api/admin/library-restore/search`, { ...auth(), params: { q: q.trim() } })).data); }
    catch { setErr("Couldn't search just now. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <AdminLayout title="Restore to my library">
      <div className="max-w-2xl space-y-4" data-testid="library-restore-page">
        <h1 className="text-2xl font-semibold">Restore to my library</h1>
        <p className="text-sm opacity-70">Find an installation by name in any account, including Recently deleted, and put it back in the library you're signed in with.</p>
        <form onSubmit={search} className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Magnetic Certainty or Horsfield" className="flex-1 rounded-lg border border-black/20 bg-transparent px-3 py-2 text-sm" data-testid="restore-search-input" />
          <button type="submit" disabled={busy} className={`${btn} bg-black text-white inline-flex items-center gap-1`} data-testid="restore-search-btn">{busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Search</button>
        </form>
        {err && <p className="text-sm text-red-600" data-testid="restore-error">{err}</p>}
        {res && (
          <>
            <p className="text-xs uppercase tracking-widest opacity-60">Installations found · {res.journeys.length}</p>
            {res.journeys.length ? res.journeys.map((j) => <JourneyRow key={j.id} j={j} onDone={() => {}} />) : <p className="text-sm opacity-60" data-testid="restore-none">No installation with that name in any account.</p>}
            {res.blueprints.length > 0 && <>
              <p className="pt-2 text-xs uppercase tracking-widest opacity-60">Or make a fresh one from the original</p>
              {res.blueprints.map((b) => <BlueprintRow key={b.id} b={b} />)}
            </>}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
